import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { activityService } from '../services/activityService';
import {
  aggregateCallMetrics,
  callMatchesUser,
  normalizeCallOutcomeSlug,
  CALL_OUTCOME_LABELS,
} from '../utils/callOutcomeAnalytics';
import {
  computeRange,
  flattenCalls,
  filterCalls,
  buildRepRows,
  RANGE_OPTS,
  loadActivityDashboardWidgets,
  saveActivityDashboardWidgets,
  DEFAULT_WIDGET_IDS,
  outcomeLabel,
  loadWidgetConfig,
  saveWidgetConfig,
} from '../utils/activityDashboardData';
import ActivityDrilldownModal from '../components/activity/ActivityDrilldownModal';
import RepComparisonTable from '../components/activity/RepComparisonTable';
import TeamMotivationBar from '../components/activity/TeamMotivationBar';
import RepDetailModal from '../components/activity/RepDetailModal';
import { buildLossCombos } from '../utils/lossCombos';

/* ── Date helpers ── */
function parseDeDate(str) {
  if (!str) return null;
  const p = str.split('.');
  if (p.length === 3) return new Date(+p[2], +p[1] - 1, +p[0]);
  return null;
}
function toYMD(d) { return d.toISOString().slice(0, 10); }
function actDate(a) {
  if (a.ts) return new Date(a.ts);
  if (a.date) return parseDeDate(a.date) || new Date(a.date);
  return null;
}
function fmtDay(ymd) {
  return new Date(ymd + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}
function fmtWeekday(ymd) {
  return new Date(ymd + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short' });
}
function trend(curr, prev) {
  if (prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

/* ── Chart primitives ── */
function HBar({ items, colorFn, maxVal, showPct = false, onItemClick }) {
  const max = maxVal ?? Math.max(...items.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, i) => (
        <div key={item.label + i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 110, fontSize: 12, color: 'var(--text2)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
          <div style={{ flex: 1, height: 22, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
            <div style={{ width: max > 0 ? `${(item.count / max) * 100}%` : '0%', height: '100%', background: colorFn ? colorFn(item, i) : '#e87040', borderRadius: 4, transition: 'width .5s ease', minWidth: item.count > 0 ? 4 : 0 }} />
          </div>
          {onItemClick ? (
            <button type="button" onClick={() => item.count > 0 && onItemClick(item)}
              style={{ width: 34, fontSize: 12, fontWeight: 700, color: item.count > 0 ? 'var(--accent)' : 'var(--text)', fontFamily: 'var(--mono)', textAlign: 'right', flexShrink: 0, background: 'none', border: 'none', cursor: item.count > 0 ? 'pointer' : 'default', textDecoration: item.count > 0 ? 'underline' : 'none', padding: 0 }}>
              {item.count}
            </button>
          ) : (
            <div style={{ width: 34, fontSize: 12, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--mono)', textAlign: 'right', flexShrink: 0 }}>{item.count}</div>
          )}
          {showPct && <div style={{ width: 40, fontSize: 11, color: 'var(--text3)', textAlign: 'right', flexShrink: 0 }}>{max > 0 ? Math.round((item.count / max) * 100) + '%' : '–'}</div>}
        </div>
      ))}
    </div>
  );
}

function VBar({ data, color = '#e87040', height = 140 }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: height + 32 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
          {d.count > 0 && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text2)', fontFamily: 'var(--mono)', lineHeight: 1 }}>{d.count}</span>}
          <div style={{ width: '100%', height: `${(d.count / max) * height}px`, background: d.count > 0 ? color : 'rgba(255,255,255,0.04)', borderRadius: '3px 3px 0 0', transition: 'height .5s ease', opacity: d.count === 0 ? 0.3 : 1, minHeight: d.count > 0 ? 3 : 0 }} title={`${d.label}: ${d.count}`} />
          <span style={{ fontSize: 9, color: 'var(--text3)', transform: 'rotate(-40deg)', transformOrigin: 'top center', whiteSpace: 'nowrap', marginTop: 2, lineHeight: 1 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function Donut({ segments, total, label = '' }) {
  let cumul = 0;
  const r = 52, cx = 60, cy = 60, sw = 14;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
      <svg width={120} height={120} viewBox="0 0 120 120" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={sw} />
        {segments.map((seg, i) => {
          const start = cumul; cumul += seg.pct;
          const dashArr = `${(seg.pct / 100) * circ} ${circ}`;
          const offset = circ * (1 - start / 100);
          return <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={sw} strokeDasharray={dashArr} strokeDashoffset={offset} transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: 'stroke-dasharray .5s' }} />;
        })}
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="20" fontWeight="800" fill="var(--text)">{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="var(--text3)" fontWeight="600">{label}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg.label}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--mono)', flexShrink: 0 }}>{Math.round(seg.pct)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendBadge({ val }) {
  if (val === null || val === undefined) return <span style={{ fontSize: 11, color: 'var(--text3)' }}>–</span>;
  const up = val >= 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: up ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: 3 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        {up ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
      </svg>
      {Math.abs(val)}%
    </span>
  );
}

/* ── Glass Card ── */
function Card({ title, badge, children, style, dragHandleProps, onResize, size }) {
  return (
    <div className="gl-card" style={{ borderRadius: 16, padding: '18px 20px', position: 'relative', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {dragHandleProps && (
          <div {...dragHandleProps} style={{ cursor: 'grab', padding: '2px 4px', borderRadius: 4, color: 'var(--text3)', flexShrink: 0, userSelect: 'none' }} title="Verschieben">
            <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
              <circle cx="3" cy="2" r="1.5" fill="currentColor" /><circle cx="9" cy="2" r="1.5" fill="currentColor" />
              <circle cx="3" cy="7" r="1.5" fill="currentColor" /><circle cx="9" cy="7" r="1.5" fill="currentColor" />
              <circle cx="3" cy="12" r="1.5" fill="currentColor" /><circle cx="9" cy="12" r="1.5" fill="currentColor" />
            </svg>
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', flex: 1, letterSpacing: '-0.01em' }}>{title}</div>
        {badge && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '2px 7px' }}>{badge}</span>}
        {onResize && (
          <button type="button" onClick={onResize} title={size === 'full' ? 'Verkleinern' : 'Vergrößern'}
            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--text3)' }}>
            {size === 'full' ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 8 3 3 8 3"/><polyline points="21 8 21 3 16 3"/><polyline points="8 21 3 21 3 16"/><polyline points="16 21 21 21 21 16"/></svg>
            )}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

const AGENT_COLORS = ['#e87040', '#159c5f', '#22c55e', '#ab9bd6', '#fb7185', '#3186ba', '#f97316', '#66796c'];

function slugBarColor(slug) {
  const map = {
    reached_appt: '#16a34a', reached_followup: '#0f8a53', reached_no: '#dc2626',
    not_voicemail: '#8f7cc9', not_busy: '#7a5fb8', not_noanswer: '#66796c',
    gatekeeper: '#3186ba', callback: '#d97706', decision_maker: '#0d9488', unknown: '#93a89a',
  };
  return map[slug] || '#93a89a';
}

const WIDGET_DEFS = [
  { id: 'motivation', label: 'Team-Motivation', desc: 'Fortschrittsbalken mit Team-Zielen' },
  { id: 'kpis', label: 'KPI-Kacheln', desc: 'Anrufe, Termine, GK, Entscheider' },
  { id: 'rep_table', label: 'Team-Vergleich', desc: 'Tabelle mit allen Mitarbeitern' },
  { id: 'agent_cards', label: 'Mitarbeiter-Karten', desc: 'Karten pro Mitarbeiter, anklickbar' },
  { id: 'outcomes', label: 'Ergebnis-Detail', desc: 'Alle Outcomes als Kacheln' },
  { id: 'by_user', label: 'Anrufe nach Nutzer', desc: 'Balkendiagramm pro Nutzer' },
  { id: 'daily_chart', label: 'Tages-Chart', desc: 'Anrufe pro Tag' },
];

/* ── Widget Picker (Drag+Resize) ── */
function WidgetPicker({ config, onChange, onClose }) {
  const [local, setLocal] = useState(config.map(w => ({ ...w })));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  function toggleVisible(id) {
    setLocal(prev => prev.map(w => w.id === id ? { ...w, visible: !w.visible } : w));
  }
  function toggleSize(id) {
    setLocal(prev => prev.map(w => w.id === id ? { ...w, size: w.size === 'full' ? 'half' : 'full' } : w));
  }
  function onDragStart(id) { setDragId(id); }
  function onDragOver(e, id) { e.preventDefault(); setOverId(id); }
  function onDropOn(id) {
    if (!dragId || dragId === id) { setDragId(null); setOverId(null); return; }
    setLocal(prev => {
      const next = [...prev];
      const from = next.findIndex(w => w.id === dragId);
      const to = next.findIndex(w => w.id === id);
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
    setDragId(null); setOverId(null);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(8,12,24,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: 'rgba(18,24,40,0.95)', borderRadius: 18, border: '1px solid rgba(255,255,255,0.1)', width: 'min(500px, 100%)', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', backdropFilter: 'blur(20px)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>Dashboard anpassen</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Reihenfolge durch Ziehen ändern · Größe pro Widget wählbar</div>
        </div>
        <div style={{ padding: '12px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {local.map(w => {
            const def = WIDGET_DEFS.find(d => d.id === w.id);
            if (!def) return null;
            const isDragging = dragId === w.id;
            const isOver = overId === w.id && dragId !== w.id;
            return (
              <div key={w.id}
                draggable
                onDragStart={() => onDragStart(w.id)}
                onDragOver={e => onDragOver(e, w.id)}
                onDrop={() => onDropOn(w.id)}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderRadius: 12, border: `1.5px solid ${isOver ? 'var(--accent-border)' : w.visible ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)'}`,
                  background: isOver ? 'rgba(117,118,201,0.1)' : w.visible ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.01)',
                  opacity: isDragging ? 0.4 : 1, cursor: 'grab', transition: 'border-color .12s, background .12s',
                }}>
                <div style={{ color: 'var(--text3)', flexShrink: 0 }}>
                  <svg width="11" height="13" viewBox="0 0 11 13" fill="none">
                    <circle cx="2.5" cy="2" r="1.5" fill="currentColor" /><circle cx="8.5" cy="2" r="1.5" fill="currentColor" />
                    <circle cx="2.5" cy="6.5" r="1.5" fill="currentColor" /><circle cx="8.5" cy="6.5" r="1.5" fill="currentColor" />
                    <circle cx="2.5" cy="11" r="1.5" fill="currentColor" /><circle cx="8.5" cy="11" r="1.5" fill="currentColor" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: w.visible ? 'var(--text)' : 'var(--text3)' }}>{def.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{def.desc}</div>
                </div>
                {/* Size toggle */}
                <button type="button" onClick={() => toggleSize(w.id)} title="Größe ändern"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, height: 28, padding: '0 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', fontSize: 11, color: 'var(--text2)', flexShrink: 0, fontWeight: 600 }}>
                  {w.size === 'full' ? (
                    <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg> Breit</>
                  ) : (
                    <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="18" rx="2"/></svg> Halb</>
                  )}
                </button>
                {/* Visible toggle */}
                <button type="button" onClick={() => toggleVisible(w.id)} style={{
                  width: 32, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', flexShrink: 0, transition: 'background .15s',
                  background: w.visible ? '#159c5f' : 'rgba(255,255,255,0.1)', position: 'relative',
                }}>
                  <span style={{ position: 'absolute', top: 2, left: w.visible ? 14 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={{ height: 36, padding: '0 16px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}>Abbrechen</button>
          <button type="button" onClick={() => { onChange(local); saveWidgetConfig(local); onClose(); }}
            style={{ height: 36, padding: '0 20px', borderRadius: 8, background: '#0f8a53', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

/* ── Draggable Widget Wrapper ── */
function DraggableWidget({ id, size, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isOver, children }) {
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; onDragStart(id); }}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver(id); }}
      onDrop={e => { e.preventDefault(); onDrop(id); }}
      onDragEnd={onDragEnd}
      style={{
        gridColumn: size === 'full' ? '1 / -1' : undefined,
        opacity: isDragging ? 0.35 : 1,
        outline: isOver ? '2px solid var(--accent)' : 'none',
        outlineOffset: 3,
        borderRadius: 18,
        transition: 'opacity .15s, outline .1s',
      }}>
      {children}
    </div>
  );
}

/* ── Main View ── */
export default function AnalyticsView({
  leads = [],
  tasks = [],
  meetings = [],
  statusMeta = {},
  authUsers = [],
  currentUser = null,
  onOpenLead,
}) {
  const isAdmin = currentUser?.role === 'admin';
  const [rangeId, setRangeId] = useState('30d');
  const [filterUserId, setFilterUserId] = useState('all');
  const [allCallsRaw, setAllCallsRaw] = useState([]);
  const [widgetConfig, setWidgetConfig] = useState(loadWidgetConfig);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [actTick, setActTick] = useState(0);
  const [selectedRep, setSelectedRep] = useState(null); // { user, idx }
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const reloadActivities = useCallback(() => {
    setAllCallsRaw(flattenCalls(leads));
    setActTick(t => t + 1);
  }, [leads]);

  useEffect(() => {
    reloadActivities();
    window.addEventListener('crm-activities-changed', reloadActivities);
    return () => window.removeEventListener('crm-activities-changed', reloadActivities);
  }, [reloadActivities]);

  useEffect(() => {
    if (isAdmin) return;
    if (currentUser?.id) setFilterUserId(currentUser.id);
  }, [isAdmin, currentUser?.id]);

  const { start, end, prevStart, prevEnd, days } = useMemo(() => computeRange(rangeId), [rangeId]);

  function inCurrent(a) { const d = actDate(a); return d && d >= start && d <= end; }
  function inPrev(a) { const d = actDate(a); return d && d >= prevStart && d <= prevEnd; }

  const calls = useMemo(() => allCallsRaw.filter(a => a.type === 'call' && inCurrent(a)), [allCallsRaw, start, end]);
  const prevCalls = useMemo(() => allCallsRaw.filter(a => a.type === 'call' && inPrev(a)), [allCallsRaw, prevStart, prevEnd]);

  const callsVisible = useMemo(
    () => (filterUserId === 'all' ? calls : calls.filter(c => callMatchesUser(c, filterUserId, authUsers))),
    [calls, filterUserId, authUsers]
  );
  const prevVisible = useMemo(
    () => (filterUserId === 'all' ? prevCalls : prevCalls.filter(c => callMatchesUser(c, filterUserId, authUsers))),
    [prevCalls, filterUserId, authUsers]
  );

  const metrics = useMemo(() => aggregateCallMetrics(callsVisible), [callsVisible]);
  const prevMetrics = useMemo(() => aggregateCallMetrics(prevVisible), [prevVisible]);

  const filteredMeetings = useMemo(() => meetings.filter(m => {
    const d = parseDeDate(m.date) || (m.date && !m.date.includes('.') ? new Date(m.date) : null);
    return d && d >= start && d <= end;
  }), [meetings, start, end]);
  const prevMeetings = useMemo(() => meetings.filter(m => {
    const d = parseDeDate(m.date) || (m.date && !m.date.includes('.') ? new Date(m.date) : null);
    return d && d >= prevStart && d <= prevEnd;
  }), [meetings, prevStart, prevEnd]);

  const callsTrend = trend(metrics.total, prevMetrics.total);
  const meetTrend = trend(filteredMeetings.length, prevMeetings.length);
  const termTrend = trend(metrics.termin, prevMetrics.termin);
  const gateTrend = trend(metrics.gatekeeper, prevMetrics.gatekeeper);
  const decTrend = trend(metrics.decider, prevMetrics.decider);

  const agents = useMemo(() => {
    if (isAdmin && authUsers.length > 0) {
      return authUsers.map((u, i) => ({
        id: u.id, label: u.name || u.email || u.id,
        count: calls.filter(c => callMatchesUser(c, u.id, authUsers)).length,
        color: AGENT_COLORS[i % AGENT_COLORS.length],
      })).sort((a, b) => b.count - a.count);
    }
    const map = {};
    calls.forEach(c => { const k = c.by || 'Unbekannt'; map[k] = (map[k] || 0) + 1; });
    return Object.entries(map).map(([label, count], i) => ({ id: label, label, count, color: AGENT_COLORS[i % AGENT_COLORS.length] })).sort((a, b) => b.count - a.count);
  }, [calls, authUsers, isAdmin]);

  const byOutcome = useMemo(() => {
    const map = {};
    callsVisible.forEach(c => {
      const slug = normalizeCallOutcomeSlug(c);
      map[slug] = (map[slug] || 0) + 1;
    });
    return Object.entries(map)
      .map(([slug, count]) => ({ slug, label: CALL_OUTCOME_LABELS[slug] || slug, count, pct: callsVisible.length ? (count / callsVisible.length) * 100 : 0, color: slugBarColor(slug) }))
      .filter(x => x.count > 0).sort((a, b) => b.count - a.count);
  }, [callsVisible]);

  const callsPerDay = useMemo(() => {
    const dayList = [];
    for (let i = Math.min(days - 1, 29); i >= 0; i--) {
      const d = new Date(end); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
      dayList.push(toYMD(d));
    }
    const map = {};
    callsVisible.forEach(c => { const d = actDate(c); if (d) { const k = toYMD(d); map[k] = (map[k] || 0) + 1; } });
    return dayList.map(day => ({ label: days <= 7 ? fmtWeekday(day) : fmtDay(day), count: map[day] || 0 }));
  }, [callsVisible, end, days]);

  const meetByType = useMemo(() => {
    const map = {};
    filteredMeetings.forEach(m => { const k = m.meetingType || m.title?.split(' ')[0] || 'Sonstiges'; map[k] = (map[k] || 0) + 1; });
    return Object.entries(map).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [filteredMeetings]);

  const meetByOutcome = useMemo(() => {
    const map = {};
    filteredMeetings.forEach(m => { const k = m.outcome || m.result || 'Kein Ergebnis'; map[k] = (map[k] || 0) + 1; });
    return Object.entries(map).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [filteredMeetings]);

  const agentStats = useMemo(() => {
    const users = isAdmin && authUsers.length > 0 ? authUsers : (currentUser ? [currentUser] : []);
    return users.map((u, idx) => {
      const myCalls = calls.filter(c => callMatchesUser(c, u.id, authUsers));
      const prevMyCalls = prevCalls.filter(c => callMatchesUser(c, u.id, authUsers));
      const m = aggregateCallMetrics(myCalls);
      const pm = aggregateCallMetrics(prevMyCalls);
      const color = AGENT_COLORS[idx % AGENT_COLORS.length];
      const ratedCalls = myCalls.filter(c => c.callQuality > 0);
      const avgQuality = ratedCalls.length > 0
        ? (ratedCalls.reduce((s, c) => s + (c.callQuality || 0), 0) / ratedCalls.length).toFixed(1)
        : null;
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      const todayEnd2 = new Date(); todayEnd2.setHours(23, 59, 59, 999);
      const todayCount = allCallsRaw.filter(c =>
        c.type === 'call' && callMatchesUser(c, u.id, authUsers) &&
        (() => { const d = actDate(c); return d && d >= todayStart && d <= todayEnd2; })()
      ).length;
      return { id: u.id, label: u.name || u.email || u.id, color, m, pm, avgQuality, idx, todayCount, trendCalls: trend(m.total, pm.total), trendTerm: trend(m.termin, pm.termin) };
    });
  }, [authUsers, calls, allCallsRaw, currentUser, isAdmin, prevCalls]);

  const rangeLabel = RANGE_OPTS.find(r => r.id === rangeId)?.label || '';
  const prevLabel = rangeId === 'heute' ? 'vs. Gestern' : `vs. Vorperiode (${rangeLabel})`;
  const filterLabel = filterUserId === 'all'
    ? 'Alle Nutzer'
    : (authUsers.find(u => String(u.id) === String(filterUserId))?.name || authUsers.find(u => String(u.id) === String(filterUserId))?.email || 'Nutzer');

  const repRows = useMemo(() => buildRepRows(calls, authUsers, isAdmin, currentUser), [calls, authUsers, isAdmin, currentUser, actTick]);

  const openDrillCalls = useCallback((title, subtitle, list) => {
    if (!list?.length) return;
    setDrilldown({ title, subtitle: subtitle || `${rangeLabel} · ${filterLabel}`, calls: list });
  }, [rangeLabel, filterLabel]);

  const lossCombos = useMemo(() => {
    const actByLead = activityService.getAll();
    return buildLossCombos(leads, actByLead).slice(0, 8);
  }, [leads, actTick]); // eslint-disable-line

  const trendWeeks = useMemo(() => {
    const now = new Date();
    const result = [];
    for (let w = 3; w >= 0; w--) {
      const wEnd = new Date(now); wEnd.setDate(now.getDate() - w * 7); wEnd.setHours(23, 59, 59, 999);
      const wStart = new Date(wEnd); wStart.setDate(wEnd.getDate() - 6); wStart.setHours(0, 0, 0, 0);
      const count = allCallsRaw.filter(c => {
        if (c.type !== 'call') return false;
        if (filterUserId !== 'all' && !callMatchesUser(c, filterUserId, authUsers)) return false;
        const d = actDate(c); return d && d >= wStart && d <= wEnd;
      }).length;
      const termine = allCallsRaw.filter(c => {
        if (c.type !== 'call') return false;
        if (filterUserId !== 'all' && !callMatchesUser(c, filterUserId, authUsers)) return false;
        const d = actDate(c); return d && d >= wStart && d <= wEnd && normalizeCallOutcomeSlug(c) === 'reached_appt';
      }).length;
      const label = `${wStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – ${wEnd.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
      result.push({ label, count, termine });
    }
    return result;
  }, [allCallsRaw, filterUserId, authUsers, actTick]); // eslint-disable-line

  const callHeatmap = useMemo(() => {
    const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
    const grid = {};
    DAYS.forEach((_, di) => { grid[di] = {}; HOURS.forEach(h => { grid[di][h] = { total: 0, success: 0 }; }); });
    const successSlugs = new Set(['reached_appt', 'decision_maker', 'reached_followup']);
    const visibleCalls = filterUserId === 'all' ? allCallsRaw : allCallsRaw.filter(c => callMatchesUser(c, filterUserId, authUsers));
    visibleCalls.forEach(c => {
      if (c.type !== 'call') return;
      let d = null;
      if (c.ts) d = new Date(c.ts);
      else if (c.date) d = parseDeDate(c.date) || new Date(c.date);
      if (!d) return;
      const jsDay = d.getDay();
      const di = jsDay === 0 ? 6 : jsDay - 1;
      const h = d.getHours();
      if (HOURS.includes(h)) {
        grid[di][h].total++;
        if (successSlugs.has(normalizeCallOutcomeSlug(c))) grid[di][h].success++;
      }
    });
    const maxTotal = Math.max(1, ...DAYS.map((_, di) => Math.max(1, ...HOURS.map(h => grid[di][h].total))));
    return { grid, DAYS, HOURS, maxTotal };
  }, [allCallsRaw, filterUserId, authUsers, actTick]); // eslint-disable-line

  const followUpStats = useMemo(() => {
    if (!isAdmin || authUsers.length === 0) return null;
    return authUsers.map(u => {
      const myTasks = tasks.filter(t => {
        if (t.done) return false;
        const owner = t.assignedTo || t.userId || t.createdBy;
        return String(owner) === String(u.id);
      });
      const overdueTasks = myTasks.filter(t => t.dueDatetime && new Date(t.dueDatetime) < new Date());
      const leadsWithWv = leads.filter(l => {
        const ownerId = l.ownerId || l.by;
        if (ownerId && String(ownerId) !== String(u.id)) return false;
        return l.wiedervorlage && new Date(l.wiedervorlage) <= new Date();
      });
      return { id: u.id, label: u.name || u.email, open: myTasks.length, overdue: overdueTasks.length, wv: leadsWithWv.length };
    }).filter(u => u.open + u.wv > 0).sort((a, b) => (b.open + b.wv) - (a.open + a.wv));
  }, [tasks, leads, authUsers, isAdmin]);

  const kpiDrillMap = useMemo(() => ({
    'Anrufe gesamt': () => callsVisible,
    'Termine gesetzt': () => callsVisible.filter(c => normalizeCallOutcomeSlug(c) === 'reached_appt'),
    'Gatekeeper': () => callsVisible.filter(c => normalizeCallOutcomeSlug(c) === 'gatekeeper'),
    'Entscheider': () => callsVisible.filter(c => normalizeCallOutcomeSlug(c) === 'decision_maker'),
    'Verbunden (geschätzt)': () => callsVisible.filter(c => !['not_voicemail', 'not_busy', 'not_noanswer'].includes(normalizeCallOutcomeSlug(c))),
  }), [callsVisible]);

  /* ── Drag-drop widget reorder ── */
  function onWidgetDragStart(id) { setDragId(id); }
  function onWidgetDragOver(id) { if (id !== dragId) setOverId(id); }
  function onWidgetDrop(id) {
    if (!dragId || dragId === id) { setDragId(null); setOverId(null); return; }
    setWidgetConfig(prev => {
      const next = [...prev];
      const from = next.findIndex(w => w.id === dragId);
      const to = next.findIndex(w => w.id === id);
      if (from < 0 || to < 0) return prev;
      next.splice(to, 0, next.splice(from, 1)[0]);
      saveWidgetConfig(next);
      return next;
    });
    setDragId(null); setOverId(null);
  }
  function toggleWidgetSize(id) {
    setWidgetConfig(prev => {
      const next = prev.map(w => w.id === id ? { ...w, size: w.size === 'full' ? 'half' : 'full' } : w);
      saveWidgetConfig(next);
      return next;
    });
  }

  const showW = id => widgetConfig.find(w => w.id === id)?.visible !== false;
  const sizeW = id => widgetConfig.find(w => w.id === id)?.size || 'full';

  const visibleWidgets = widgetConfig.filter(w => w.visible !== false);

  /* ── Render a widget given its id ── */
  function renderWidget(wid) {
    const size = sizeW(wid);
    const dragProps = {
      onDragStart: onWidgetDragStart,
      onDragOver: onWidgetDragOver,
      onDrop: onWidgetDrop,
      onDragEnd: () => { setDragId(null); setOverId(null); },
      isDragging: dragId === wid,
      isOver: overId === wid && dragId !== wid,
    };
    const handleProps = {
      draggable: false,
      onMouseDown: e => e.stopPropagation(),
    };

    const cardProps = {
      dragHandleProps: handleProps,
      onResize: () => toggleWidgetSize(wid),
      size,
    };

    switch (wid) {
      case 'motivation':
        if (!isAdmin) return null;
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <TeamMotivationBar repRows={repRows} teamGoal={50} rangeLabel={rangeLabel} />
          </DraggableWidget>
        );

      case 'kpis':
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <div style={{ borderRadius: 16, padding: '18px 20px', background: 'var(--bg2)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <div style={{ ...dragHandle }} title="Verschieben" {...handleProps} draggable={false}>
                  <DragIcon />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', flex: 1 }}>KPI-Übersicht</div>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '2px 7px' }}>{rangeLabel}</span>
                <button type="button" onClick={() => toggleWidgetSize(wid)} title={size === 'full' ? 'Verkleinern' : 'Vergrößern'} style={resizeBtn}>
                  <ResizeIcon expanded={size === 'full'} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {[
                  { label: 'Anrufe gesamt', value: metrics.total, t: callsTrend, color: '#e87040', icon: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/> },
                  { label: 'Termine gesetzt', value: metrics.termin, t: termTrend, sub: `${metrics.pctTermin}% · ${metrics.pctTerminVsReached}% vs. verbunden`, color: '#16a34a', icon: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></> },
                  { label: 'Gatekeeper', value: metrics.gatekeeper, t: gateTrend, sub: `${metrics.pctGate}% aller Anrufe`, color: '#3186ba', icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
                  { label: 'Entscheider', value: metrics.decider, t: decTrend, sub: `${metrics.pctDecider}% aller Anrufe`, color: '#0d9488', icon: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></> },
                  { label: 'Verbunden', value: metrics.reached, t: null, sub: 'ohne reine Nicht-Erreichbarkeit', color: '#66796c', icon: <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
                  { label: 'Meetings', value: filteredMeetings.length, t: meetTrend, color: '#8f7cc9', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
                ].map(kpi => {
                  const drillFn = kpiDrillMap[kpi.label];
                  const canDrill = drillFn && kpi.value > 0;
                  return (
                    <button key={kpi.label} type="button" onClick={() => canDrill && openDrillCalls(kpi.label, kpi.sub, drillFn())}
                      style={{ background: 'var(--bg3)', borderRadius: 12, border: '1px solid var(--border)', padding: '14px 16px', textAlign: 'left', cursor: canDrill ? 'pointer' : 'default', width: '100%', transition: 'border-color .12s' }}
                      onMouseEnter={e => { if (canDrill) e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.3 }}>{kpi.label}</div>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: kpi.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={kpi.color} strokeWidth="2">{kpi.icon}</svg>
                        </div>
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 900, color: 'var(--text)', lineHeight: 1, letterSpacing: '-0.02em' }}>{kpi.value}</div>
                      {kpi.sub && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 5, lineHeight: 1.35 }}>{kpi.sub}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <TrendBadge val={kpi.t} />
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{prevLabel}</span>
                      </div>
                      {canDrill && <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 7, fontWeight: 600 }}>Liste anzeigen →</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          </DraggableWidget>
        );

      case 'rep_table':
        if (!isAdmin) return null;
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <Card title="Team-Vergleich" badge={`${rangeLabel} · Klick auf Zahl`} {...cardProps}>
              <RepComparisonTable rows={repRows} rangeLabel={rangeLabel}
                onCellClick={({ row, col }) => {
                  let list = row.calls;
                  if (col.slug !== 'total') list = row.calls.filter(c => normalizeCallOutcomeSlug(c) === col.slug);
                  openDrillCalls(`${row.label} · ${col.label}`, rangeLabel, list);
                }} />
            </Card>
          </DraggableWidget>
        );

      case 'agent_cards':
        if (agentStats.length === 0) return null;
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <div style={{ borderRadius: 16, padding: '18px 20px', background: 'var(--bg2)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <div style={{ ...dragHandle }} title="Verschieben" {...handleProps} draggable={false}><DragIcon /></div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', flex: 1 }}>
                  {isAdmin ? 'Mitarbeiter-Übersicht' : 'Deine Kennzahlen'}
                </div>
                {isAdmin && <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>Klick = Details</span>}
                <button type="button" onClick={() => toggleWidgetSize(wid)} title={size === 'full' ? 'Verkleinern' : 'Vergrößern'} style={resizeBtn}>
                  <ResizeIcon expanded={size === 'full'} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {agentStats.map(ag => (
                  <button key={ag.id} type="button"
                    onClick={() => { if (isAdmin) setSelectedRep({ user: authUsers.find(u => u.id === ag.id) || { id: ag.id, name: ag.label }, idx: ag.idx }); }}
                    style={{
                      background: 'var(--bg3)', borderRadius: 14, border: `1.5px solid ${String(filterUserId) === String(ag.id) ? ag.color : 'var(--border)'}`,
                      padding: '16px 18px', textAlign: 'left', cursor: isAdmin ? 'pointer' : 'default',
                      transition: 'border-color .15s, transform .1s, box-shadow .15s', width: '100%',
                    }}
                    onMouseEnter={e => { if (isAdmin) { e.currentTarget.style.borderColor = ag.color; e.currentTarget.style.boxShadow = `0 4px 20px ${ag.color}22`; } }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = String(filterUserId) === String(ag.id) ? ag.color : 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: ag.color + '20', border: `2px solid ${ag.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: ag.color, flexShrink: 0, boxShadow: `0 0 12px ${ag.color}33` }}>
                        {ag.label.split(' ').map(x => x[0] || '').join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ag.label}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{rangeLabel}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                        {ag.todayCount > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#e87040', background: 'rgba(232,112,64,0.12)', borderRadius: 6, padding: '2px 7px' }}>
                            Heute: {ag.todayCount}
                          </span>
                        )}
                        {ag.avgQuality && (
                          <span style={{ fontSize: 12, color: '#f59e0b' }}>{'★'.repeat(Math.round(ag.avgQuality))}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
                      {[
                        { label: 'Anrufe', val: ag.m.total, color: '#159c5f', trend2: ag.trendCalls },
                        { label: 'Termine', val: ag.m.termin, color: '#16a34a', trend2: ag.trendTerm },
                        { label: 'Gatekeeper', val: ag.m.gatekeeper, color: '#3186ba', trend2: null },
                        { label: 'Entscheider', val: ag.m.decider, color: '#0d9488', trend2: null },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--bg4,rgba(0,0,0,0.15))', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{s.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.val}</div>
                          {s.trend2 !== null && <TrendBadge val={s.trend2} />}
                        </div>
                      ))}
                    </div>
                    <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${ag.m.pctTermin}%`, background: `linear-gradient(90deg, ${ag.color}, #16a34a)`, borderRadius: 2, transition: 'width .5s' }} title={`Terminquote: ${ag.m.pctTermin}%`} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 9, color: 'var(--text3)' }}>
                      <span>Terminquote {ag.m.pctTermin}%</span>
                      {isAdmin && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>Details anzeigen →</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </DraggableWidget>
        );

      case 'outcomes':
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <Card title="Outcomes im Detail" badge={`${filterLabel} · Klick = Liste`} {...cardProps}>
              {byOutcome.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>Keine Daten.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                  {byOutcome.map(oc => {
                    const prevCount = prevVisible.filter(c => normalizeCallOutcomeSlug(c) === oc.slug).length;
                    const tr = trend(oc.count, prevCount);
                    return (
                      <button key={oc.slug} type="button"
                        onClick={() => openDrillCalls(oc.label, rangeLabel, callsVisible.filter(c => normalizeCallOutcomeSlug(c) === oc.slug))}
                        style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)', textAlign: 'left', cursor: 'pointer', width: '100%', transition: 'border-color .12s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = oc.color + '66'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: oc.color, flexShrink: 0 }} />
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{oc.label}</div>
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 900, color: oc.color, lineHeight: 1 }}>{oc.count}</div>
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}><TrendBadge val={tr} /></div>
                        <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 6, fontWeight: 600 }}>Liste →</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          </DraggableWidget>
        );

      case 'by_user':
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <div style={{ display: 'grid', gridTemplateColumns: size === 'half' ? '1fr' : '1fr 1fr', gap: 14 }}>
              <Card title="Anrufe nach Nutzer" badge={rangeLabel} {...cardProps}>
                {agents.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, paddingTop: 20 }}>Noch keine Anrufe.</div>
                ) : (
                  <HBar items={agents} colorFn={(item, i) => item.color || AGENT_COLORS[i % AGENT_COLORS.length]}
                    onItemClick={(item) => {
                      const u = authUsers.find(x => (x.name || x.email) === item.label || String(x.id) === String(item.id));
                      const list = u ? calls.filter(c => callMatchesUser(c, u.id, authUsers)) : calls.filter(c => (c.by || '') === item.label);
                      openDrillCalls(`Anrufe · ${item.label}`, rangeLabel, list);
                    }} />
                )}
              </Card>
              <Card title="Anruf-Ergebnisse" badge={rangeLabel} dragHandleProps={null} onResize={null} size={null}>
                {byOutcome.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, paddingTop: 20 }}>Keine Ergebnis-Daten.</div>
                ) : (
                  <Donut segments={byOutcome.slice(0, 7)} total={callsVisible.length} label="ANRUFE" />
                )}
              </Card>
            </div>
          </DraggableWidget>
        );

      case 'daily_chart':
        return (
          <DraggableWidget key={wid} id={wid} size={size} {...dragProps}>
            <Card title="Getätigte Anrufe nach Tag" badge={`${rangeLabel} · gefiltert`} {...cardProps}>
              {callsPerDay.every(d => d.count === 0) ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>Keine Anrufe im Zeitraum.</div>
              ) : (
                <VBar data={callsPerDay} color="#e87040" height={160} />
              )}
            </Card>
          </DraggableWidget>
        );

      default:
        return null;
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', background: 'var(--bg)', fontFamily: 'var(--font)' }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Aktivitäts-Dashboard</div>
          {filterUserId !== 'all' && (
            <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, marginTop: 2 }}>{filterLabel}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setShowWidgetPicker(true)} style={{ height: 34, padding: '0 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Dashboard anpassen
          </button>
          {isAdmin && authUsers.length > 0 && (
            <select value={filterUserId} onChange={e => setFilterUserId(e.target.value)}
              style={{ height: 34, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, padding: '0 10px', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', minWidth: 200 }}>
              <option value="all">Alle Nutzer (Team)</option>
              {authUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email || u.id}{u.email && u.name ? ` · ${u.email}` : ''}</option>
              ))}
            </select>
          )}
          <div style={{ display: 'flex', background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {RANGE_OPTS.map(r => (
              <button key={r.id} onClick={() => setRangeId(r.id)}
                style={{ height: 34, padding: '0 14px', fontSize: 12, fontWeight: rangeId === r.id ? 700 : 400, cursor: 'pointer', border: 'none', background: rangeId === r.id ? '#0f8a53' : 'transparent', color: rangeId === r.id ? '#fff' : 'var(--text2)', transition: 'all .12s' }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Widget Canvas ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {visibleWidgets.map(w => renderWidget(w.id))}
      </div>

      {/* ── Static sections (not in widget config) ── */}

      {/* Verloren-Analyse */}
      {lossCombos.length > 0 && (
        <div className="gl-card" style={{ borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            Verloren-Analyse
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)', marginLeft: 2 }}>Häufigste Verlustgründe</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
            {lossCombos.map((c, i) => {
              const pct = lossCombos[0].count > 0 ? (c.count / lossCombos[0].count) * 100 : 0;
              return (
                <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{c.lossReason}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 6, padding: '1px 7px' }}>{c.count}×</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginBottom: 6 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#ef4444', borderRadius: 2, transition: 'width .3s' }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', gap: 6 }}>
                    {c.branch !== '—' && <span>Branche: {c.branch}</span>}
                    {c.lastCallSlug !== 'unknown' && <span>· Letzter Anruf: {c.lastCallSlug}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Aktivitäts-Trend 4 Wochen */}
      {trendWeeks.some(w => w.count > 0) && (
        <div className="gl-card" style={{ borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(29,190,116,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            </div>
            Aktivitäts-Trend <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>Letzte 4 Wochen</span>
          </div>
          {(() => {
            const maxCount = Math.max(1, ...trendWeeks.map(w => w.count));
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {trendWeeks.map((w, i) => {
                  const pct = (w.count / maxCount) * 100;
                  const termPct = w.count > 0 ? Math.round((w.termine / w.count) * 100) : 0;
                  return (
                    <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 8 }}>{w.label}</div>
                      <div style={{ height: 64, display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 8 }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <div style={{ width: '100%', background: '#159c5f', borderRadius: '4px 4px 0 0', height: `${Math.max(4, pct * 0.64)}px`, transition: 'height .3s' }} />
                          <div style={{ width: '100%', background: '#22c55e', borderRadius: '4px 4px 0 0', height: `${Math.max(2, (w.termine / maxCount) * 64)}px`, transition: 'height .3s' }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{w.count}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>Anrufe · <span style={{ color: '#22c55e', fontWeight: 600 }}>{w.termine} Termine ({termPct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Heatmap */}
      {allCallsRaw.filter(c => c.type === 'call').length > 10 && (
        <div className="gl-card" style={{ borderRadius: 14, padding: '18px 20px', marginBottom: 20, overflowX: 'auto' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            Beste Anrufzeiten <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>Wochentag × Uhrzeit (Erfolg)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${callHeatmap.HOURS.length}, 1fr)`, gap: 3, minWidth: 500 }}>
            <div />
            {callHeatmap.HOURS.map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', fontFamily: 'var(--mono)' }}>{h}h</div>
            ))}
            {callHeatmap.DAYS.map((day, di) => (
              <React.Fragment key={di}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', alignSelf: 'center' }}>{day}</div>
                {callHeatmap.HOURS.map(h => {
                  const cell = callHeatmap.grid[di][h];
                  const intensity = cell.total > 0 ? cell.total / callHeatmap.maxTotal : 0;
                  const hasSuccess = cell.success > 0;
                  const bg = cell.total === 0 ? 'var(--bg3)' : hasSuccess ? `rgba(34,197,94,${0.15 + intensity * 0.65})` : `rgba(29,190,116,${0.1 + intensity * 0.55})`;
                  return (
                    <div key={h} title={`${day} ${h}h: ${cell.total} Anrufe, ${cell.success} Erfolge`}
                      style={{ height: 28, borderRadius: 5, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: cell.total ? (hasSuccess ? '#15803d' : '#0d7647') : 'transparent', fontWeight: 700 }}>
                      {cell.total > 0 ? cell.total : ''}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 14, fontSize: 10, color: 'var(--text3)' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(34,197,94,0.6)', verticalAlign: 'middle', marginRight: 4 }} />Mit Termin/Erfolg</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(29,190,116,0.4)', verticalAlign: 'middle', marginRight: 4 }} />Anrufe ohne Erfolg</span>
          </div>
        </div>
      )}

      {/* Follow-up */}
      {followUpStats && followUpStats.length > 0 && (
        <div className="gl-card" style={{ borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(168,85,247,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a678cf" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
            </div>
            Follow-up-Übersicht <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>Offene Aufgaben & WV pro Rep</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {followUpStats.map(u => (
              <div key={u.id} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{u.label}</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { val: u.open, label: 'Aufgaben', color: u.open > 0 ? '#159c5f' : 'var(--text3)' },
                    { val: u.overdue, label: 'Überfällig', color: u.overdue > 0 ? '#ef4444' : 'var(--text3)' },
                    { val: u.wv, label: 'WV fällig', color: u.wv > 0 ? '#f59e0b' : 'var(--text3)' },
                  ].map((s, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <div style={{ width: 1, background: 'var(--border)' }} />}
                      <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
                        <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {calls.length === 0 && filteredMeetings.length === 0 && (
        <div className="gl-card" style={{ borderRadius: 14, padding: '60px 24px', textAlign: 'center', marginTop: 20 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Noch keine Aktivitätsdaten</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
            Logge Anrufe im Lead-Panel — wähle dabei das Ergebnis (Gatekeeper, Entscheider, Termin …). Als Admin kannst du oben einzelne Nutzer filtern.
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showWidgetPicker && (
        <WidgetPicker config={widgetConfig} onChange={setWidgetConfig} onClose={() => setShowWidgetPicker(false)} />
      )}

      <ActivityDrilldownModal
        open={!!drilldown}
        title={drilldown?.title || ''}
        subtitle={drilldown?.subtitle}
        calls={drilldown?.calls || []}
        onClose={() => setDrilldown(null)}
        onOpenLead={lead => { setDrilldown(null); onOpenLead?.(lead); }}
      />

      {selectedRep && (
        <RepDetailModal
          user={selectedRep.user}
          userIdx={selectedRep.idx}
          allCallsRaw={allCallsRaw}
          leads={leads}
          authUsers={authUsers}
          onClose={() => setSelectedRep(null)}
          onOpenLead={lead => { setSelectedRep(null); onOpenLead?.(lead); }}
        />
      )}
    </div>
  );
}

/* ── Shared micro-styles ── */
const dragHandle = {
  cursor: 'grab', padding: '2px 4px', borderRadius: 4, color: 'var(--text3)', flexShrink: 0, userSelect: 'none', display: 'flex', alignItems: 'center',
};
const resizeBtn = {
  width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--text3)',
};

function DragIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
      <circle cx="3" cy="2" r="1.5" fill="currentColor" /><circle cx="9" cy="2" r="1.5" fill="currentColor" />
      <circle cx="3" cy="7" r="1.5" fill="currentColor" /><circle cx="9" cy="7" r="1.5" fill="currentColor" />
      <circle cx="3" cy="12" r="1.5" fill="currentColor" /><circle cx="9" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

function ResizeIcon({ expanded }) {
  return expanded ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
      <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 8 3 3 8 3"/><polyline points="21 8 21 3 16 3"/>
      <polyline points="8 21 3 21 3 16"/><polyline points="16 21 21 21 21 16"/>
    </svg>
  );
}
